import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert, Button, Menu, MenuItem } from '@mui/material';

import { Task } from '@/types';
import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';
import {
  TaskTable,
  useTaskTableActions,
  MY_TASKS_CONFIG,
} from '@/shared/components/TaskTable';

interface TaskListViewProps {
  tasks: Task[];
  isLoading: boolean;
  currentRaciRole?: 'responsible' | 'accountable' | 'consulted' | 'informed';
}

/**
 * TaskListView
 * Table-based task list with quick actions (desktop)
 * Card-based task list for mobile
 * Optimized for scanning and bulk operations
 * 
 * Now uses shared TaskTable component for consistency across the app
 */
const TaskListView: React.FC<TaskListViewProps> = ({ 
  tasks, 
  isLoading, 
  currentRaciRole 
}) => {
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTask, setMenuTask] = useState<Task | null>(null);

  // Use shared task table actions hook
  const {
    handleCheckboxChange,
    handleDelete,
    handleComplete,
    confirmDelete,
    confirmCompleteTask,
    confirmDeleteTask,
    blockingWarning,
    blockingTask,
    setConfirmCompleteTask,
    setConfirmDeleteTask,
    clearBlockingWarning,
  } = useTaskTableActions({
    context: 'myTasks',
    allTasks: tasks,
  });

  /**
   * Navigate to task detail via query param
   */
  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks?taskId=${taskId}`);
  };

  /**
   * View blocking task
   */
  const handleViewBlocker = () => {
    if (blockingTask) {
      handleTaskClick(blockingTask.id);
      clearBlockingWarning();
    }
  };

  /**
   * Handle menu actions (three-dot menu)
   */
  const handleMenuAction = (action: string, task: Task, event?: React.MouseEvent<HTMLElement>) => {
    if (action === 'menu' && event) {
      event.stopPropagation();
      setMenuAnchor(event.currentTarget);
      setMenuTask(task);
    }
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTask(null);
  };

  const handleMenuView = () => {
    if (menuTask) {
      navigate(`/tasks?taskId=${menuTask.id}`);
    }
    handleMenuClose();
  };

  const handleMenuDelete = () => {
    if (menuTask) {
      setConfirmDeleteTask(menuTask);
    }
    handleMenuClose();
  };

  const isAdminOrManager = (role?: string) => {
    return role && ['super_admin', 'admin', 'manager'].includes(role);
  };

  return (
    <>
      {/* Main Task Table */}
      <TaskTable
        tasks={tasks}
        isLoading={isLoading}
        context="myTasks"
        config={MY_TASKS_CONFIG}
        currentRaciRole={currentRaciRole}
        onTaskClick={handleTaskClick}
        onCheckboxChange={handleCheckboxChange}
        onMenuAction={(action, task, event) => {
          if (action === 'menu' && event) {
            setMenuAnchor(event.currentTarget);
            setMenuTask(task);
          }
        }}
      />

      {/* Task Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 160 } }}
      >
        <MenuItem onClick={handleMenuView}>View details</MenuItem>
        {menuTask && (
          isAdminOrManager(user?.role) || 
          (user?.role === 'engineer' && menuTask.createdBy === user?.id)
        ) && (
          <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Complete Task Confirmation Modal */}
      {confirmCompleteTask && (
        <ConfirmationModal
          open={true}
          onClose={() => setConfirmCompleteTask(null)}
          data={{
            title: 'Mark as completed',
            message: `Are you sure you want to mark "${confirmCompleteTask.title}" as completed?`,
            type: 'success',
            confirmText: 'Complete',
            onConfirm: () => handleComplete(confirmCompleteTask),
          }}
        />
      )}

      {/* Delete Task Confirmation Modal */}
      {confirmDeleteTask && (
        <ConfirmationModal
          open={true}
          onClose={() => setConfirmDeleteTask(null)}
          data={{
            title: 'Delete task',
            message: `Are you sure you want to delete "${confirmDeleteTask.title}"? This action cannot be undone.`,
            type: 'danger',
            confirmText: 'Delete',
            onConfirm: () => confirmDelete(confirmDeleteTask),
          }}
        />
      )}

      {/* Blocking Warning Snackbar */}
      <Snackbar
        open={!!blockingWarning}
        autoHideDuration={6000}
        onClose={clearBlockingWarning}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80, md: 24 } }}
      >
        <Alert
          onClose={clearBlockingWarning}
          severity="warning"
          sx={{ width: '100%' }}
          action={
            blockingTask && (
              <Button color="inherit" size="small" onClick={handleViewBlocker}>
                View Blocker
              </Button>
            )
          }
        >
          {blockingWarning}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TaskListView;
